import React from 'react';
import { Box, Typography } from '@mui/material';
import { colors, typography } from '../../../styles/theme';
import ListingImageCarousel from '../DockPanel/Tabs/ListingImageCarousel';

const TEXT_STRONG = '#EDEDED';
const TEXT_MUTED = '#ABABAB';

const captionSx = {
  fontFamily: typography.fontFamily.primary,
  fontSize: '10px',
  fontWeight: 600,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  lineHeight: '14px',
};

/**
 * Cuerpo de presentación marketplace (título → descripción → precio → metas → fotos).
 * Compartido entre cards de lista branded y la sección de contenido del lote en el dock.
 *
 * @param {'row'|'column'} metasLayout - 'row' (default) reparte las metas en columnas;
 *   'column' las apila una debajo de otra (card de lote solo catastro en grilla de 2).
 * @param {boolean} wrapText - título y descripción bajan de línea en vez de cortarse
 *   (cards de Registros). No cambia tamaño ni mayúsculas del título.
 * @param {boolean} compact - card de registro: menos separación y monto más chico.
 * @param {React.ReactNode} titleAdornment - al lado del título (p. ej. ícono de contenido de lote).
 * @param {boolean} singleLine - card de registro de alto fijo: título, descripción y
 *   caption en una línea con ellipsis (texto completo en `title`). Pisa `wrapText`.
 */
export default function BrandedListingContent({
  title = null,
  description = null,
  descriptionClamp = 2,
  hero = null,
  metas = [],
  images = [],
  accentColor = null,
  dense = false,
  metasLayout = 'row',
  wrapText = false,
  compact = false,
  titleAdornment = null,
  singleLine = false,
}) {
  const ellipsisSx = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };
  const textTitle = (value) => (singleLine && typeof value === 'string' ? value : undefined);
  const accent = accentColor || colors.primary;
  const isPriceHero = hero?.kind === 'price';
  const stackMetas = metasLayout === 'column';
  const imageRows = Array.isArray(images) ? images.filter((image) => image?.url) : [];

  const hasBody = Boolean(
    title
    || description
    || hero
    || metas.length > 0
    || imageRows.length > 0,
  );
  if (!hasBody) return null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: compact ? '6px' : dense ? '9px' : '11px', minWidth: 0 }}>
      {title && titleAdornment ? (
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', minWidth: 0 }}>
          <Typography
            title={textTitle(title)}
            sx={{
              fontFamily: typography.fontFamily.heading,
              fontSize: '14px',
              fontWeight: 600,
              lineHeight: '18px',
              color: TEXT_STRONG,
              minWidth: 0,
              ...(singleLine ? ellipsisSx : { whiteSpace: 'normal', wordBreak: 'break-word' }),
            }}
          >
            {title}
          </Typography>
          <Box component="span" sx={{ display: 'inline-flex', mt: '1px', flexShrink: 0 }}>{titleAdornment}</Box>
        </Box>
      ) : title && (
        <Typography
          title={textTitle(title)}
          sx={{
            fontFamily: typography.fontFamily.heading,
            fontSize: descriptionClamp === false ? '16px' : '14px',
            fontWeight: 600,
            lineHeight: descriptionClamp === false ? '22px' : '18px',
            color: TEXT_STRONG,
            textTransform: descriptionClamp === false ? 'uppercase' : 'none',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: descriptionClamp === false || wrapText ? 'normal' : 'nowrap',
            wordBreak: descriptionClamp === false || wrapText ? 'break-word' : undefined,
            ...(singleLine ? ellipsisSx : null),
          }}
        >
          {title}
        </Typography>
      )}

      {description && (
        <Typography
          title={textTitle(description)}
          sx={{
            fontFamily: typography.fontFamily.primary,
            fontSize: '12px',
            lineHeight: '16px',
            color: TEXT_MUTED,
            mt: title ? (compact ? '-3px' : '-4px') : 0,
            ...(singleLine ? ellipsisSx : descriptionClamp === false || wrapText
              ? { whiteSpace: 'pre-wrap', wordBreak: 'break-word' }
              : {
                display: '-webkit-box',
                WebkitLineClamp: descriptionClamp,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
              }),
          }}
        >
          {description}
        </Typography>
      )}

      {hero && (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: singleLine ? 'nowrap' : 'wrap', minWidth: 0 }}>
            <Typography
              title={textTitle(hero.caption)}
              sx={{ ...captionSx, color: isPriceHero ? accent : TEXT_MUTED, ...(singleLine ? { ...ellipsisSx, minWidth: 0 } : null) }}
            >
              {hero.caption}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontFamily: typography.fontFamily.heading,
              fontSize: compact ? (dense ? '17px' : '20px') : dense ? '19px' : '26px',
              fontWeight: 700,
              lineHeight: compact ? (dense ? '22px' : '24px') : dense ? '25px' : '32px',
              letterSpacing: '-0.01em',
              color: '#FFFFFF',
              mt: compact ? '1px' : '2px',
              ...(singleLine ? ellipsisSx : null),
            }}
          >
            {hero.amount}
          </Typography>
        </Box>
      )}

      {metas.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: stackMetas
              ? 'minmax(0, 1fr)'
              : dense && metas.some((meta) => meta.key === 'stock')
              ? `repeat(${Math.max(metas.filter((meta) => meta.key !== 'stock').length, 1)}, minmax(0, 1fr))`
              : `repeat(${metas.length}, minmax(0, 1fr))`,
            gap: stackMetas ? '8px' : dense ? '6px' : '10px',
          }}
        >
          {metas.map((meta) => {
            const isNode = React.isValidElement(meta.value);
            return (
              <Box
                key={meta.key}
                sx={{
                  minWidth: 0,
                  ...(dense && meta.key === 'stock' ? { gridColumn: '1 / -1' } : null),
                }}
              >
                <Typography
                  sx={{
                    ...captionSx,
                    color: TEXT_MUTED,
                    ...(dense ? { fontSize: '9.5px', letterSpacing: '0.04em' } : null),
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {(dense && meta.shortLabel) || meta.label}
                </Typography>
                <Box
                  component={isNode ? 'div' : 'p'}
                  sx={{
                    fontFamily: typography.fontFamily.primary,
                    fontSize: dense ? '12.5px' : '13.5px',
                    fontWeight: 500,
                    lineHeight: '18px',
                    color: TEXT_STRONG,
                    mt: '2px',
                    mb: 0,
                    minWidth: 0,
                    ...(isNode
                      ? { display: 'flex', overflow: 'hidden' }
                      : {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }),
                  }}
                >
                  {meta.value}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {imageRows.length > 0 && (
        <ListingImageCarousel images={imageRows} />
      )}
    </Box>
  );
}
